/**
 * Subscription Plans Configuration
 * 
 * Central configuration for all subscription plans.
 * The PricingPage reads from this config dynamically.
 * 
 * When ENABLE_PAYMENTS=true, prices will be used for Stripe integration.
 * When ENABLE_PAYMENTS=false, upgrades are manual/trial only.
 *
 * Sprint 3.1 — Legacy UI/marketing catalog: commercial source of truth for limits/pricing
 * is `billing_plans` (+ merged fallbacks in `server/billing-access.js`). Keep this file for
 * PricingPage until the SPA reads the DB-backed catalog endpoint.
 */

export const PLANS = {
  free: {
    id: 'free',
    name: 'Free',
    nameAr: 'مجاني',
    currency: 'SAR',
    monthlyPrice: 0,
    annualPrice: 0,
    price: 0,
    priceDisplay: 'Free',
    priceDisplayAr: 'مجاني',
    billingPeriod: null,
    description: 'Perfect for getting started',
    descriptionAr: 'مثالي للبدء',
    features: [
      'Basic legal consultations',
      'Limited chat messages',
      'Standard response time',
      'Community support',
    ],
    featuresAr: [
      'استشارات قانونية أساسية',
      'رسائل محادثة محدودة',
      'وقت استجابة قياسي',
      'دعم المجتمع',
    ],
    quotas: {
      chatMessages: 50,        // per month
      memorandums: 2,          // per month
      documents: 5,            // per month
      apiCalls: 100,           // per month
    },
    limits: {
      maxMessageLength: 2000,
      maxFilesPerMessage: 2,
      maxFileSize: 5 * 1024 * 1024, // 5MB
    },
    highlighted: false,
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    nameAr: 'احترافي',
    currency: 'SAR',
    monthlyPrice: 99,
    annualPrice: 990,
    price: 99,
    priceDisplay: '99 SAR/month',
    priceDisplayAr: '99 ر.س شهرياً',
    billingPeriod: 'monthly',
    description: 'For professionals and businesses',
    descriptionAr: 'للمحترفين والشركات',
    features: [
      'Unlimited legal consultations',
      'Unlimited chat messages',
      'Priority support',
      'Advanced AI features',
      'Document analysis',
    ],
    featuresAr: [
      'استشارات قانونية غير محدودة',
      'رسائل محادثة غير محدودة',
      'دعم ذو أولوية',
      'ميزات ذكاء اصطناعي متقدمة',
      'تحليل المستندات',
    ],
    quotas: {
      chatMessages: -1,        // -1 = unlimited
      memorandums: 20,         // per month
      documents: 50,           // per month
      apiCalls: 1000,          // per month
    },
    limits: {
      maxMessageLength: 10000,
      maxFilesPerMessage: 10,
      maxFileSize: 25 * 1024 * 1024, // 25MB
    },
    highlighted: true,
  },
  office: {
    id: 'office',
    name: 'Office',
    nameAr: 'مكتب',
    currency: 'SAR',
    monthlyPrice: 499,
    annualPrice: 4990,
    price: 499,
    priceDisplay: '499 SAR/month',
    priceDisplayAr: '499 ر.س شهرياً',
    billingPeriod: 'monthly',
    description: 'For legal firms and office teams',
    descriptionAr: 'للمكاتب القانونية والفرق',
    features: [
      'Everything in Pro',
      'Office workspace',
      'Office case management',
      'Higher team capacity',
      'Priority support',
    ],
    featuresAr: [
      'كل مزايا احترافي',
      'مساحة عمل للمكتب',
      'إدارة قضايا المكتب',
      'سعة أعلى للفريق',
      'دعم ذو أولوية',
    ],
    quotas: {
      chatMessages: -1,
      memorandums: -1,
      documents: -1,
      apiCalls: -1,
    },
    limits: {
      maxMessageLength: -1,
      maxFilesPerMessage: -1,
      maxFileSize: 100 * 1024 * 1024,
    },
    highlighted: true,
    audience: 'firm',
  },
  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    nameAr: 'المؤسسات',
    currency: 'SAR',
    monthlyPrice: null,
    annualPrice: null,
    price: null,
    contactOnly: true,
    priceDisplay: 'Contact us',
    priceDisplayAr: 'تواصل معنا',
    billingPeriod: 'custom',
    description: 'For large organizations',
    descriptionAr: 'للمؤسسات الكبيرة',
    features: [
      'Everything in Pro',
      'Dedicated account manager',
      'Custom integrations',
      'SLA guarantees',
      'On-premise deployment options',
    ],
    featuresAr: [
      'كل ما في الخطة الاحترافية',
      'مدير حساب مخصص',
      'تكاملات مخصصة',
      'ضمانات اتفاقية مستوى الخدمة',
      'خيارات النشر الداخلية',
    ],
    quotas: {
      chatMessages: -1,        // unlimited
      memorandums: -1,         // unlimited
      documents: -1,           // unlimited
      apiCalls: -1,            // unlimited
    },
    limits: {
      maxMessageLength: -1,   // unlimited
      maxFilesPerMessage: -1,  // unlimited
      maxFileSize: 100 * 1024 * 1024, // 100MB
    },
    highlighted: false,
  },
};

/**
 * Format plan price for pricing UI (aligned with billing_plans catalog).
 * @param {string} planId
 * @param {'monthly' | 'annual'} billingInterval
 * @param {boolean} [isRTL=false]
 */
export const getPlanPriceDisplay = (planId, billingInterval = 'monthly', isRTL = false) => {
  const plan = getPlan(planId);
  if (!plan) return '—';

  if (plan.id === 'free') {
    return isRTL ? plan.priceDisplayAr || 'مجاني' : plan.priceDisplay || 'Free';
  }

  if (plan.contactOnly || plan.monthlyPrice == null) {
    return isRTL ? plan.priceDisplayAr || 'تواصل معنا' : plan.priceDisplay || 'Contact us';
  }

  const amount = billingInterval === 'annual' ? plan.annualPrice : plan.monthlyPrice;
  const currency = plan.currency || 'SAR';

  if (billingInterval === 'annual') {
    return isRTL ? `${amount} ${currency} سنوياً` : `${amount} ${currency}/year`;
  }
  return isRTL ? `${amount} ${currency} شهرياً` : `${amount} ${currency}/month`;
};

/**
 * Get plan by ID
 */
export const getPlan = (planId) => {
  return PLANS[planId] || PLANS.free;
};

/**
 * Get all plans as array
 */
export const getAllPlans = () => {
  return Object.values(PLANS);
};

/**
 * Check if plan has unlimited quota for a resource
 */
export const isUnlimited = (planId, resource) => {
  const plan = getPlan(planId);
  const quota = plan.quotas[resource];
  return quota === -1 || quota === null;
};

/**
 * Get quota for a resource in a plan
 */
export const getQuota = (planId, resource) => {
  const plan = getPlan(planId);
  return plan.quotas[resource] || 0;
};

/**
 * Check if payments are enabled
 */
export const isPaymentsEnabled = () => {
  return process.env.REACT_APP_ENABLE_PAYMENTS === 'true';
};

export default PLANS;

